import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowRight, Loader2, Lock, ShieldCheck, User } from 'lucide-react'

import { Button } from '@/components/ui'
import { appRoutes } from '@/constants'
import { queryClient } from '@/lib/query-client'
import { getApiErrorCode, getApiErrorMessage } from '@/lib/api-client'
import { authApi } from '../api'
import { authQueryKeys, useLogin } from '../hooks'
import { loginSchema, type LoginValues } from '../schemas'
import { AuthLayout } from './auth-layout'
import { AuthField } from './auth-field'
import { zodResolver } from './zod-resolver'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [submitting, setSubmitting] = useState(false)
  const loginMutation = useLogin()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true)
    try {
      await loginMutation.mutateAsync(values)
      const me = await queryClient.fetchQuery({
        queryKey: authQueryKeys.me(),
        queryFn: () => authApi.me(),
      })
      navigate(me.redirect.path, { replace: true })
    } catch (error) {
      const code = getApiErrorCode(error)
      if (code === 'EMAIL_NOT_VERIFIED') {
        toast.warning('Email not verified', {
          description: 'Please verify your email before signing in.',
        })
        navigate(`${appRoutes.auth.verificationPending}?email=${encodeURIComponent(values.email)}`, {
          replace: true,
        })
        return
      }
      toast.error(getApiErrorMessage(error, 'Unable to sign in'))
    } finally {
      setSubmitting(false)
    }
  })

  const from = (location.state as { from?: string } | null)?.from

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to access your dashboard and dealership portal."
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link to={appRoutes.auth.register} className="font-semibold text-primary hover:underline">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
          label="Email Address"
          icon={<User />}
          placeholder="you@example.com"
          type="email"
          autoComplete="email"
          error={errors.email}
          {...register('email')}
        />
        <AuthField
          label="Password"
          icon={<Lock />}
          placeholder="••••••••"
          type="password"
          autoComplete="current-password"
          error={errors.password}
          {...register('password')}
        />

        <div className="flex justify-end">
          <Link
            to={appRoutes.auth.forgotPassword}
            className="text-xs font-medium text-muted-foreground hover:text-primary"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          {submitting ? 'Signing in...' : 'Sign In & Continue'}
          {!submitting ? <ArrowRight className="size-4" /> : null}
        </Button>
      </form>

      {from ? (
        <p className="mt-4 text-center text-xs text-muted-foreground">
          You need to sign in to access <span className="text-foreground">{from}</span>
        </p>
      ) : null}

      <div className="mt-6 flex items-center justify-center gap-2 border-t border-white/10 pt-6 text-xs text-muted-foreground">
        <ShieldCheck className="size-4 text-primary" />
        <span>
          For dealers and sellers:{' '}
          <Link to={appRoutes.auth.login} className="font-semibold text-primary hover:underline">
            use the same account
          </Link>
        </span>
      </div>
    </AuthLayout>
  )
}